import TagManager from "@sooro-io/react-gtm-module";

import type { ICart } from "../interface/cart";
import type { ICalculatedDraftOrder } from "../interface/draft-order";
import type { MailingAddress, Order, Product, ProductVariant } from "../types/storefront.types";

const APP_NAME = import.meta.env.VITE_REACT_APP_NAME as string;

/** select_item: variant가 없는 상품 카드용 기본 값 */
interface ISelectItemFields {
  handle: string;
  title: string;
  price: number | string;
  compareAtPrice?: number | string;
  availableForSale?: boolean | null;
}

/** 결제 금액: INTL은 배송비·세금·관세가 포함된 calculatedOrder 기준 */
const getCheckoutValue = (cart: ICart["cart"], calculatedOrder?: ICalculatedDraftOrder) =>
  Number(calculatedOrder?.totalPriceSet.shopMoney.amount ?? cart.cost.totalAmount.amount);

/** begin_checkout / add_shipping_info / add_payment_info 공통 items */
const getCheckoutItems = (cart: ICart["cart"]) =>
  cart.lines.edges.map(({ node }, index) => {
    const variant = node.merchandise;
    return {
      index,
      item_variant_id: variant.id.replace("gid://shopify/ProductVariant/", ""),
      item_id: variant.sku,
      item_name: variant.product.title,
      item_brand: variant.product.vendor,
      item_variant_name: variant.title,
      item_category: variant.product.productType,
      quantity: node.quantity,
      price: Number(variant.price.amount),
      compare_at_price: Number(variant.compareAtPrice?.amount),
      available: variant.availableForSale,
      image: variant.image?.url,
    };
  });

export default function useTracking() {
  const gtmInit = () => {
    const lang = localStorage.getItem("i18nextLng");
    const GTM_KEY = {
      tune: "GTM-MKXPWZ2T",
      yeseyesee: lang === "ko" ? "GTM-TXM3GKTJ" : "GTM-PCGT6G5N",
      thisisneverthat: lang === "ko" ? "GTM-P7WWMKDB" : lang === "jp" ? "GTM-M3RCBLV8" : "GTM-W2XJNPD8",
      khakis: lang === "ko" ? "GTM-M4QMGQ6Q" : "GTM-PG22DBLF",
      sunlove: lang === "ko" ? "GTM-WN2L5TPH" : "GTM-W94RHRVV",
      wellbeingexpress: "GTM-PGZG8NMZ",
    };
    if (GTM_KEY[APP_NAME as keyof typeof GTM_KEY] !== "") {
      TagManager.initialize({
        gtmId: GTM_KEY[APP_NAME as keyof typeof GTM_KEY],
      });
    }
  };

  const gtmViewItem = (product: Product, customerId?: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "view_item",
        user_id: customerId || undefined,
        item_id: product.id.replace("gid://shopify/Product/", ""),
        item_name: product.title,
        item_brand: product.vendor,
        item_category: product.productType,
        price: product.priceRange.maxVariantPrice.amount,
        compare_at_price: product.compareAtPriceRange.maxVariantPrice.amount,
        available: product.availableForSale,
        image: product.featuredImage?.url,
        currency: product.priceRange.maxVariantPrice.currencyCode,
      },
    });
  };

  const gtmAddToCart = (variant: ProductVariant, customerId?: string, customerEmail?: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "add_to_cart",
        user_id: customerId || undefined,
        user_email: customerEmail || undefined,
        ecommerce: {
          items: [
            {
              item_variant_id: variant.id.replace("gid://shopify/ProductVariant/", ""),
              item_id: variant.sku,
              item_name: variant.product.title,
              item_brand: variant.product.vendor,
              item_variant_name: variant.title,
              item_category: variant.product.productType,
              quantity: 1,
              price: Number(variant.price.amount),
              compare_at_price: Number(variant.compareAtPrice?.amount),
              available: variant.availableForSale,
              image: variant.image?.url,
            },
          ],
          value: Number(variant.price.amount),
          currency: variant.price.currencyCode,
        },
      },
    });
  };

  const gtmAddToWishList = (variant: ProductVariant, customerId?: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "add_to_wishlist",
        user_id: customerId || undefined,
        ecommerce: {
          items: [
            {
              item_variant_id: variant.id.replace("gid://shopify/ProductVariant/", ""),
              item_name: variant.product.title,
              quantity: 1,
            },
          ],
          value: Number(variant.price.amount),
          currency: variant.price.currencyCode,
        },
      },
    });
  };

  /** 상품 목록에서 상품 클릭 (variant가 없는 카드는 기본 값만, 어느 목록인지는 이벤트의 page_location으로 구분) */
  const gtmSelectItem = (item: ProductVariant | ISelectItemFields, customerId?: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "select_item",
        user_id: customerId || undefined,
        ecommerce: {
          items: [
            "product" in item
              ? {
                  item_variant_id: item.id.replace("gid://shopify/ProductVariant/", ""),
                  item_id: item.sku,
                  item_name: item.product.title,
                  item_brand: item.product.vendor,
                  item_variant_name: item.title,
                  item_category: item.product.productType,
                  price: Number(item.price.amount),
                  compare_at_price: Number(item.compareAtPrice?.amount),
                  available: item.availableForSale,
                  image: item.image?.url,
                }
              : {
                  item_id: item.handle,
                  item_name: item.title,
                  price: Number(item.price),
                  compare_at_price: Number(item.compareAtPrice ?? 0),
                  available: item.availableForSale ?? undefined,
                },
          ],
        },
      },
    });
  };

  const gtmPurchase = (order: Order, customerId?: string, customerEmail?: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "purchase",
        user_id: customerId,
        user_email: customerEmail || order.email,
        ecommerce: {
          order_id: order.name,
          order_total_amount: Number(order.totalPrice.amount),
          order_total_quantity: order.lineItems.edges.length,
          currency: order.totalPrice.currencyCode,
          order_items: order.lineItems.edges.map(({ node }, index) => {
            return {
              index,
              item_variant_id: node.variant?.id.replace("gid://shopify/ProductVariant/", ""),
              item_id: node.variant?.sku,
              item_name: node.variant?.product.title,
              item_brand: node.variant?.product.vendor,
              item_variant_name: node.variant?.title,
              item_category: node.variant?.product.productType,
              quantity: Number(node.quantity),
              price: Number(node.discountedTotalPrice.amount) / Number(node.quantity),
            };
          }),
        },
      },
    });
  };

  const gtmLocationChanged = (location: { pathname: string }, customerId?: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "location_changed",
        user_id: customerId || undefined,
        page: location.pathname,
      },
    });
  };

  const gtmLogin = (method: "email" | "social", customerId?: string, customerEmail?: string): void => {
    TagManager.dataLayer({
      dataLayer: {
        event: "login",
        method,
        user_id: customerId || undefined,
        user_email: customerEmail || undefined,
      },
    });
  };

  /** 체크아웃 시작 (begin_checkout) */
  const gtmBeginCheckout = (cart: ICart["cart"], customerId?: string, customerEmail?: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "begin_checkout",
        user_id: customerId || undefined,
        user_email: customerEmail || undefined,
        ecommerce: {
          value: Number(cart.cost.totalAmount.amount),
          currency: cart.cost.totalAmount.currencyCode,
          items: getCheckoutItems(cart),
        },
      },
    });
  };

  /** 배송지 기준 배송비·세금·관세 계산 완료 (INTL은 calculatedOrder 기준 금액, 국가는 배송지 우선) */
  const gtmAddShippingInfo = (
    cart: ICart["cart"],
    calculatedOrder?: ICalculatedDraftOrder,
    shippingAddress?: MailingAddress,
    customerId?: string,
  ) => {
    const shippingOption = cart.deliveryGroups.edges
      .flatMap(({ node }) => node.deliveryOptions)
      .find((option) => option.deliveryMethodType === "SHIPPING");
    const taxAmount = Number(calculatedOrder?.taxLines[0]?.priceSet.shopMoney.amount || 0);
    const totalTaxAmount = Number(calculatedOrder?.totalTaxSet.shopMoney.amount || 0);

    TagManager.dataLayer({
      dataLayer: {
        event: "add_shipping_info",
        user_id: customerId || undefined,
        shipping_country: shippingAddress?.countryCodeV2 || cart.buyerIdentity.countryCode || undefined,
        shipping_amount: Number(
          (calculatedOrder ? calculatedOrder.shippingLine?.discountedPriceSet.shopMoney.amount : shippingOption?.estimatedCost.amount) || 0,
        ),
        tax_amount: taxAmount,
        duty_amount: totalTaxAmount - taxAmount,
        ecommerce: {
          value: getCheckoutValue(cart, calculatedOrder),
          currency: cart.cost.totalAmount.currencyCode,
          shipping_tier: shippingOption?.title,
          items: getCheckoutItems(cart),
        },
      },
    });
  };

  /** 결제 수단 선택 후 결제창 요청 직전 */
  const gtmAddPaymentInfo = (cart: ICart["cart"], paymentType: string, calculatedOrder?: ICalculatedDraftOrder, customerId?: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "add_payment_info",
        user_id: customerId || undefined,
        ecommerce: {
          value: getCheckoutValue(cart, calculatedOrder),
          currency: cart.cost.totalAmount.currencyCode,
          payment_type: paymentType,
          items: getCheckoutItems(cart),
        },
      },
    });
  };

  /** 카트의 체크아웃 버튼 클릭 (로그인 확인 전이라 비로그인 이탈까지 집계) */
  const gtmCheckoutClick = (loggedIn: boolean) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "checkout_click",
        logged_in: loggedIn,
      },
    });
  };

  /** 결제 버튼 클릭 (결제 전 검증 시작 시점) */
  const gtmPayClick = (customerId?: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "pay_click",
        user_id: customerId || undefined,
      },
    });
  };

  /** 체크아웃 에러 (검증 실패, 결제 실패, 결제 위젯·배송비 계산 실패, 세션 만료) */
  const gtmCheckoutError = (errorType: string, errorReason: string, customerId?: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "checkout_error",
        user_id: customerId || undefined,
        error_type: errorType,
        error_reason: errorReason,
      },
    });
  };

  /** 404 노출 (from_path: 404로 보내기 전 원래 경로) */
  const gtmPageNotFound = (fromPath: string, reason: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: "page_not_found",
        from_path: fromPath,
        not_found_reason: reason,
      },
    });
  };

  return {
    gtmInit,
    gtmAddToCart,
    gtmAddToWishList,
    gtmViewItem,
    gtmSelectItem,
    gtmPurchase,
    gtmBeginCheckout,
    gtmAddShippingInfo,
    gtmAddPaymentInfo,
    gtmLocationChanged,
    gtmLogin,
    gtmCheckoutClick,
    gtmPayClick,
    gtmCheckoutError,
    gtmPageNotFound,
  };
}
